import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAppContext } from '@/app/providers/AppContextProvider';
import Spinner from '@/components/shared/Spinner';
import {
	buildConfirmedPayload,
	isSourceTrusted
} from '@/features/Quote/services/confirmationWizard';
import { cn } from '@/lib/utils';
import type { SettingType } from '@/types/app';
import Alert from './settings/Alert';
import AbsenceDetails from './settings/absence/DetailsPanel';
import AbsenceSummary from './settings/absence/SummaryPanel';
import CoverageDetails from './settings/coverage/DetailsPanel';
import CoverageSummary from './settings/coverage/SummaryPanel';
import EmployeesDetails from './settings/employees/DetailsPanel';
import EmployeesSummary from './settings/employees/SummaryPanel';

interface SettingsProps {
	loading?: boolean;
	setIsEditingSettings: (isEditing: boolean) => void;
	onOpenSetting?: (
		openSettingFn: (settingType: SettingType, fromQuoteSelection?: boolean) => void
	) => void;
	/** Re-fetch quotes after a confirmed recalculation (owned by Quote.tsx). */
	onRecalculate: () => void;
}

const Settings = ({
	loading = false,
	setIsEditingSettings,
	onOpenSetting,
	onRecalculate
}: SettingsProps) => {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const [activeSetting, setActiveSetting] = useState<SettingType | null>(null); // Track which setting is active (null = none active)
	const [openedFromQuoteSelection, setOpenedFromQuoteSelection] = useState(false); // Track if opened from quote selection
	// Set when the deferred recalculation request fails, so the user gets feedback that
	// their confirmed changes were not saved (and can retry by re-confirming).
	const [commitFailed, setCommitFailed] = useState(false);
	const settingsCardRef = useRef<HTMLDivElement>(null);
	const isEditingRef = useRef(false); // Track current isEditing state to avoid unnecessary calls
	const flushingRef = useRef(false); // Guards the deferred commit against double-firing
	// Steps whose deferred save has already been sent this session. Kept in state (not a
	// ref) so that dropping a step here — once its source turns trusted — re-runs the
	// reconcile effect below. Until then the step stays in `confirmedSteps` (card green,
	// guidance advanced); this set is what stops the held confirmation from re-triggering
	// the commit on every intervening poll.
	const [committedSteps, setCommittedSteps] = useState<ReadonlySet<SettingType>>(new Set());
	const {
		appData,
		pendingPremiumVars,
		premiumVarManagement,
		setIsSubmitting,
		// Steps the user has confirmed during the current confirmation-wizard session.
		// Lives in context (not local state) so the top guidance bar reads the same set
		// and can't name a step the user just confirmed. Lets the wizard route through
		// every remaining warning before a single recalculation, so confirming one step
		// never silently confirms another.
		confirmedSteps,
		markStepConfirmed,
		clearConfirmedSteps
	} = useAppContext();

	const absenceInfoSource = useMemo(
		() => appData?.premium_variables?.absence_info?.source || null,
		[appData?.premium_variables?.absence_info?.source]
	);
	const employeesSource = useMemo(
		() => appData?.premium_variables?.employees?.source || null,
		[appData?.premium_variables?.employees?.source]
	);
	const coverageSource = useMemo(
		() => appData?.premium_variables?.coverage?.sick_leave_short_term?.source || null,
		[appData?.premium_variables?.coverage?.sick_leave_short_term?.source]
	);

	// Re-fetch quotes after a recalculation. Quote.tsx owns the single
	// useAppStatePolling instance and restart, so this delegates to its callback.
	const handleTriggerAppStatePolling = useCallback(() => {
		onRecalculate();
	}, [onRecalculate]);

	// Deferred commit: when the wizard fully closes (last Volgende, "Bevestigen en
	// herberekenen", X, or click-outside) commit exactly the cards the user confirmed
	// this session in a single recalculation. A card that was opened/edited but never
	// confirmed is not in `confirmedSteps`, so it is omitted (discarded). Per-step
	// Volgende keeps `activeSetting` non-null, so nothing is saved until the very end.
	useEffect(() => {
		if (activeSetting !== null || flushingRef.current || !id) return;

		// Only send steps confirmed this session that we have not sent yet. Confirmations
		// are held (not cleared) after a commit until their source turns trusted, so this
		// filter is what keeps the effect from re-saving them on every intervening poll.
		const stepsToCommit = new Set([...confirmedSteps].filter((step) => !committedSteps.has(step)));
		if (stepsToCommit.size === 0) return;

		flushingRef.current = true;
		setIsSubmitting(true);
		setCommitFailed(false);
		setCommittedSteps((prev) => new Set([...prev, ...stepsToCommit]));

		const commit = async () => {
			try {
				await premiumVarManagement.save(
					id,
					buildConfirmedPayload(stepsToCommit, {
						employeeList: appData?.premium_variables?.employees?.employee_list ?? [],
						absenceValues: pendingPremiumVars?.absenceValues ?? null,
						coverageValues: pendingPremiumVars?.coverageValues ?? null
					})
				);
				handleTriggerAppStatePolling();
			} catch (error) {
				console.error('Failed to submit confirmed settings:', error);
				// The save didn't persist, so these steps' sources will never turn trusted.
				// Roll the optimistic confirmation fully back (cards return to a warning) and
				// surface the error so the user re-confirms to retry — clearing `confirmedSteps`
				// too is also what stops the rolled-back `committedSteps` from re-triggering this
				// effect into a save-retry loop while the request keeps failing.
				clearConfirmedSteps([...stepsToCommit]);
				setCommittedSteps((prev) => {
					const next = new Set(prev);
					for (const step of stepsToCommit) next.delete(step);
					return next;
				});
				setCommitFailed(true);
			} finally {
				setIsSubmitting(false);
				flushingRef.current = false;
			}
		};
		commit();
	}, [
		activeSetting,
		confirmedSteps,
		committedSteps,
		id,
		appData,
		pendingPremiumVars,
		premiumVarManagement,
		setIsSubmitting,
		handleTriggerAppStatePolling,
		clearConfirmedSteps
	]);

	// Reconcile the optimistic session confirmations against the server. The deferred
	// save's own response carries no premium_variables, so a confirmed step's source only
	// turns trusted via the recalculation's poll — which can take seconds. We keep the
	// confirmation (green check + advanced guidance) until then, and drop it only once the
	// source is trusted, so a just-confirmed step never flickers back to a warning (and the
	// guidance bar never jumps back to it) mid-recalculation.
	useEffect(() => {
		const sourceByStep: Record<SettingType, string | null> = {
			employees: employeesSource,
			absence: absenceInfoSource,
			coverage: coverageSource
		};
		const settled = (Object.keys(sourceByStep) as SettingType[]).filter(
			(step) =>
				confirmedSteps.has(step) && committedSteps.has(step) && isSourceTrusted(sourceByStep[step])
		);
		if (settled.length === 0) return;
		setCommittedSteps((prev) => {
			const next = new Set(prev);
			for (const step of settled) next.delete(step);
			return next;
		});
		clearConfirmedSteps(settled);
	}, [
		confirmedSteps,
		committedSteps,
		employeesSource,
		absenceInfoSource,
		coverageSource,
		clearConfirmedSteps
	]);

	const setActiveSettingAndEditingState = useCallback(
		(newActiveSetting: SettingType | null) => {
			setActiveSetting(newActiveSetting);
			// Opening a card to (re)confirm clears any previous recalculation error.
			if (newActiveSetting !== null) {
				setCommitFailed(false);
			}
			const newIsEditing = newActiveSetting !== null;
			if (isEditingRef.current !== newIsEditing) {
				isEditingRef.current = newIsEditing;
				// Defer the setState call to avoid updating parent during render
				setTimeout(() => setIsEditingSettings(newIsEditing), 0);
			}
		},
		[setIsEditingSettings]
	);

	const handleSettingClick = useCallback(
		(settingType: SettingType) => {
			setActiveSetting((currentActive) => {
				const newActiveSetting = currentActive === settingType ? null : settingType;
				const newIsEditing = newActiveSetting !== null;
				if (isEditingRef.current !== newIsEditing) {
					isEditingRef.current = newIsEditing;
					// Defer the setState call to avoid updating parent during render
					setTimeout(() => setIsEditingSettings(newIsEditing), 0);
				}
				return newActiveSetting;
			});
			// Reset the quote selection flag when user manually clicks settings
			setOpenedFromQuoteSelection(false);
		},
		[setIsEditingSettings]
	);

	// Expose method to programmatically open specific settings
	const openSetting = useCallback(
		(settingType: SettingType, fromQuoteSelection = false) => {
			setActiveSettingAndEditingState(settingType);
			setOpenedFromQuoteSelection(fromQuoteSelection);

			// Scroll to top when panel is opened from quote selection so user can see it
			if (fromQuoteSelection) {
				window.scrollTo({ top: 0, behavior: 'smooth' });
			}
		},
		[setActiveSettingAndEditingState]
	);

	// Expose the openSetting function to parent component
	useEffect(() => {
		if (onOpenSetting) {
			onOpenSetting(openSetting);
		}
	}, [onOpenSetting, openSetting]);

	// Handle clicks outside the settings card to deactivate all settings
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			// Use composedPath() to traverse shadow DOM boundaries
			const path = event.composedPath();

			// Check if click is inside any settings-related element (including shadow DOM)
			const isInsideSettings = path.some((element) => {
				if (!(element instanceof HTMLElement)) return false;

				// Check if inside the settings card ref
				if (settingsCardRef.current?.contains(element)) return true;
				if (element === settingsCardRef.current) return true;

				// Check if element has data-edit-*-card attribute (detail panels)
				if (
					element.hasAttribute?.('data-edit-employees-card') ||
					element.hasAttribute?.('data-edit-absence-card') ||
					element.hasAttribute?.('data-edit-coverage-card')
				) {
					return true;
				}

				// Check if inside settings details container
				if (element.hasAttribute?.('data-settings-details')) return true;
				if (element.closest?.('[data-settings-details]')) return true;

				return false;
			});

			if (isInsideSettings) return;

			// Check if click is on scrollbar or scrollbar-related elements
			const target = event.target as HTMLElement;
			if (target) {
				const isScrollbarClick = (() => {
					// Check if target is a scrollbar element
					if (target.tagName === 'HTML' || target.tagName === 'BODY') {
						return true;
					}

					// Check if click is near the right edge of viewport (scrollbar area)
					const viewportWidth = window.innerWidth;
					const clickX = event.clientX;
					const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

					// If click is within scrollbar area (typically 15-17px from right edge)
					if (scrollbarWidth > 0 && clickX >= viewportWidth - scrollbarWidth) {
						return true;
					}

					// Check if target has scrollbar-related properties
					const computedStyle = window.getComputedStyle(target);
					if (computedStyle.overflowY === 'scroll' || computedStyle.overflowY === 'auto') {
						// Check if click is within the scrollbar track area
						const rect = target.getBoundingClientRect();
						const scrollbarTrackWidth = rect.width - target.clientWidth;
						if (scrollbarTrackWidth > 0 && clickX >= rect.right - scrollbarTrackWidth) {
							return true;
						}
					}

					return false;
				})();

				// Don't close panel if click is on scrollbar
				if (isScrollbarClick) {
					return;
				}
			}

			// Close panel for genuine outside clicks
			setActiveSettingAndEditingState(null);
			setOpenedFromQuoteSelection(false);
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [setActiveSettingAndEditingState]);

	if (employeesSource === 'not_applicable') return null;

	return (
		<div className="mb-7">
			{commitFailed && (
				<Alert
					variant="danger"
					className="mb-2"
					showIcon
					testId="settings-commit-error"
					text={t('quotes.settings.recalculationError')}
				/>
			)}
			<div
				data-testid="settings"
				data-settings
				ref={settingsCardRef}
				className={cn(
					'w-full bg-white border border-border mb-2 relative rounded flex select-none',
					loading && 'pointer-events-none opacity-75'
				)}
			>
				<div className="flex flex-col sm:flex-row w-full overflow-hidden rounded">
					<EmployeesSummary
						handleSettingClick={handleSettingClick}
						activeSetting={activeSetting}
						employeesSource={employeesSource}
						loading={loading}
						idx={0}
						isConfirmed={confirmedSteps.has('employees')}
					/>
					<AbsenceSummary
						activeSetting={activeSetting}
						handleSettingClick={handleSettingClick}
						absenceInfoSource={absenceInfoSource}
						loading={loading}
						idx={1}
						isConfirmed={confirmedSteps.has('absence')}
					/>
					<CoverageSummary
						activeSetting={activeSetting}
						handleSettingClick={handleSettingClick}
						loading={loading}
						idx={2}
						isConfirmed={confirmedSteps.has('coverage')}
					/>
					{loading && (
						<div
							data-testid="spinner"
							className="absolute inset-0 flex items-center justify-center rounded bg-white/75 opacity-75"
						>
							<Spinner size="md" variant="primary" />
						</div>
					)}
				</div>
			</div>

			<div data-settings-details className="relative w-full">
				<EmployeesDetails
					activeSetting={activeSetting}
					setActiveSetting={setActiveSettingAndEditingState}
					handleTriggerAppStatePolling={handleTriggerAppStatePolling}
					openedFromQuoteSelection={openedFromQuoteSelection}
					employeesSource={employeesSource}
					confirmedSteps={confirmedSteps}
					markStepConfirmed={markStepConfirmed}
				/>
				<AbsenceDetails
					activeSetting={activeSetting}
					setActiveSetting={setActiveSettingAndEditingState}
					openedFromQuoteSelection={openedFromQuoteSelection}
					confirmedSteps={confirmedSteps}
					markStepConfirmed={markStepConfirmed}
				/>
				<CoverageDetails
					activeSetting={activeSetting}
					setActiveSetting={setActiveSettingAndEditingState}
					openedFromQuoteSelection={openedFromQuoteSelection}
					confirmedSteps={confirmedSteps}
					markStepConfirmed={markStepConfirmed}
				/>
			</div>
		</div>
	);
};

export default Settings;
